import { useRef, useState, useCallback, useEffect } from "react";
import { Share2, Download, Copy, X, Loader2, Check } from "lucide-react";

const TIER_STYLES = {
  cadet:     { color: "#94a3b8", glow: "#cbd5e1" },
  recruit:   { color: "#22d3ee", glow: "#67e8f9" },
  pilot:     { color: "#33e0ff", glow: "#7dd3fc" },
  captain:   { color: "#c084fc", glow: "#d8b4fe" },
  commander: { color: "#fbbf24", glow: "#fde68a" },
};

// Draw a stylised pilot rank card onto the given canvas (1200x630).
async function drawCard(ctx, W, H, data) {
  const { name, score, level, tierLabel, tierKey, skinName, skinColor, skinGlow } = data;
  const style = TIER_STYLES[tierKey] || TIER_STYLES.cadet;

  // Background gradient (deep space)
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#020617");
  bg.addColorStop(0.5, "#0a0f1f");
  bg.addColorStop(1, "#050107");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Starfield
  for (let i = 0; i < 220; i++) {
    ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.85 + 0.15})`;
    const r = Math.random() * 1.6;
    ctx.beginPath();
    ctx.arc(Math.random() * W, Math.random() * H, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Nebula glow spots
  for (const [x, y, color] of [[200, 460, style.glow], [980, 180, skinGlow || "#33e0ff"]]) {
    const rad = ctx.createRadialGradient(x, y, 0, x, y, 260);
    rad.addColorStop(0, `${color}44`);
    rad.addColorStop(1, "transparent");
    ctx.fillStyle = rad;
    ctx.fillRect(0, 0, W, H);
  }

  // Top-left brand
  ctx.fillStyle = "#33e0ff";
  ctx.font = "600 22px 'Space Mono', monospace";
  ctx.fillText("$UFO COIN // PILOT PASSPORT", 60, 60);

  // Big pilot name
  ctx.fillStyle = "#ffffff";
  ctx.font = "900 96px 'Space Grotesk', system-ui, sans-serif";
  ctx.fillText(name.toUpperCase(), 60, 190);
  // subtle stroke behind name
  ctx.strokeStyle = "#33e0ff33";
  ctx.lineWidth = 2;
  ctx.strokeText(name.toUpperCase(), 60, 190);

  // Divider line
  ctx.strokeStyle = "#33e0ff55";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(60, 220);
  ctx.lineTo(720, 220);
  ctx.stroke();

  // Score block (huge)
  ctx.fillStyle = "#33e0ff";
  ctx.font = "600 20px 'Space Mono', monospace";
  ctx.fillText("MELHOR SCORE", 60, 275);
  ctx.fillStyle = "#ffffff";
  ctx.font = "900 140px 'Space Grotesk', system-ui, sans-serif";
  ctx.fillText(score.toLocaleString(), 60, 400);

  // Level chip
  ctx.fillStyle = "#a855f7";
  ctx.font = "600 20px 'Space Mono', monospace";
  ctx.fillText(`LEVEL ${level}`, 60, 445);

  // Rank badge (right side)
  const badgeX = W - 400, badgeY = 260, badgeW = 340, badgeH = 260;
  ctx.strokeStyle = style.color;
  ctx.lineWidth = 3;
  ctx.strokeRect(badgeX, badgeY, badgeW, badgeH);
  // inner glow
  const inner = ctx.createLinearGradient(badgeX, badgeY, badgeX, badgeY + badgeH);
  inner.addColorStop(0, `${style.color}22`);
  inner.addColorStop(1, "transparent");
  ctx.fillStyle = inner;
  ctx.fillRect(badgeX + 3, badgeY + 3, badgeW - 6, badgeH - 6);
  // "RANK" label
  ctx.fillStyle = "#94a3b8";
  ctx.font = "600 20px 'Space Mono', monospace";
  ctx.fillText("RANK GALÁCTICO", badgeX + 24, badgeY + 44);
  // Tier label — auto-shrink to fit badge width
  const badgeInnerW = badgeW - 48;
  let tierFontSize = 64;
  ctx.font = `900 ${tierFontSize}px 'Space Grotesk', system-ui, sans-serif`;
  while (ctx.measureText(tierLabel).width > badgeInnerW && tierFontSize > 28) {
    tierFontSize -= 4;
    ctx.font = `900 ${tierFontSize}px 'Space Grotesk', system-ui, sans-serif`;
  }
  ctx.fillStyle = style.color;
  ctx.fillText(tierLabel, badgeX + 24, badgeY + 118);
  // Ship name
  ctx.fillStyle = "#ffffff";
  ctx.font = "600 22px 'Space Mono', monospace";
  ctx.fillText("NAVE ATUAL:", badgeX + 24, badgeY + 168);
  ctx.fillStyle = skinGlow || "#33e0ff";
  ctx.font = "700 22px 'Space Mono', monospace";
  // wrap ship name if needed
  const maxTxtWidth = badgeW - 48;
  let txt = skinName;
  while (ctx.measureText(txt).width > maxTxtWidth && txt.length > 3) txt = txt.slice(0, -1);
  if (txt.length < skinName.length) txt = txt.slice(0, -1) + "…";
  ctx.fillText(txt, badgeX + 24, badgeY + 200);

  // Bottom bar — CTA
  ctx.fillStyle = "#33e0ff";
  ctx.font = "700 22px 'Space Mono', monospace";
  ctx.fillText("JOGUE // TRANSMITA O SINAL", 60, H - 60);
  ctx.fillStyle = "#94a3b8";
  ctx.font = "500 18px 'Space Mono', monospace";
  const host = (typeof window !== "undefined" ? window.location.host : "$UFO");
  ctx.fillText(host + "/pilot", 60, H - 30);

  // Bottom-right: little UFO glyph
  ctx.strokeStyle = style.color;
  ctx.lineWidth = 3;
  const gx = W - 130, gy = H - 60;
  ctx.beginPath();
  ctx.ellipse(gx, gy, 55, 15, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = `${skinGlow || "#33e0ff"}88`;
  ctx.beginPath();
  ctx.ellipse(gx, gy - 10, 22, 12, 0, 0, Math.PI * 2);
  ctx.fill();
}

export default function ShareRankCard({ open, onClose, stats, equippedSkin }) {
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const render = useCallback(async () => {
    if (!canvasRef.current || !stats) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    await drawCard(ctx, c.width, c.height, {
      name: stats.display_name || "PILOTO",
      score: stats.best_score || 0,
      level: stats.best_level || 1,
      tierLabel: stats.tier?.label || "CADETE",
      tierKey: stats.tier?.tier || "cadet",
      skinName: equippedSkin?.name || "UFO Clássico",
      skinColor: equippedSkin?.color || "#334466",
      skinGlow: equippedSkin?.glow_color || "#33e0ff",
    });
  }, [stats, equippedSkin]);

  useEffect(() => {
    if (open) {
      // small delay so canvas is mounted
      setTimeout(render, 40);
    }
  }, [open, render]);

  const download = () => {
    const c = canvasRef.current; if (!c) return;
    setBusy(true);
    c.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ufo-pilot-${(stats?.display_name || "rank").toLowerCase()}.png`;
      a.click();
      URL.revokeObjectURL(url);
      setBusy(false);
    }, "image/png", 0.95);
  };

  const share = async () => {
    const c = canvasRef.current; if (!c) return;
    setBusy(true);
    c.toBlob(async (blob) => {
      try {
        const file = new File([blob], "ufo-pilot-rank.png", { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: "Meu rank $UFO Pilot",
            text: `Sou ${stats?.tier?.label} com ${stats?.best_score?.toLocaleString()} pontos no $UFO Pilot 🛸`,
          });
        } else {
          download();
        }
      } catch (err) {
        // User cancelled the share picker — this is expected behavior
        console.debug("[share] user cancelled or share unsupported:", err?.name);
      }
      setBusy(false);
    }, "image/png", 0.95);
  };

  const copy = async () => {
    const c = canvasRef.current; if (!c) return;
    setBusy(true);
    c.toBlob(async (blob) => {
      try {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } catch (err) {
        console.warn("[share] clipboard write failed, falling back to download:", err);
        download();
      }
      setBusy(false);
    }, "image/png", 0.95);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-6" data-testid="share-rank-modal">
      <div className="w-full max-w-3xl rounded-sm border border-primary/40 bg-gradient-to-b from-[#050a18] to-[#020408] p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-secondary">// TRANSMISSÃO SOCIAL</p>
            <h2 className="font-display font-black text-2xl tracking-tighter text-white">COMPARTILHE SEU RANK</h2>
          </div>
          <button onClick={onClose} data-testid="share-close" className="p-2 border border-primary/30 rounded-sm hover:bg-primary/10 text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="rounded-sm border border-primary/20 bg-black overflow-hidden">
          <canvas
            ref={canvasRef}
            width={1200}
            height={630}
            className="w-full h-auto block"
            data-testid="share-rank-canvas"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button onClick={share} disabled={busy} data-testid="share-btn" className="inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono tracking-widest text-[10px] px-4 py-3 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />} COMPARTILHAR
          </button>
          <button onClick={download} disabled={busy} data-testid="download-btn" className="inline-flex items-center justify-center gap-2 rounded-sm border border-secondary/50 hover:bg-secondary/10 text-secondary font-mono tracking-widest text-[10px] px-4 py-3 disabled:opacity-50">
            <Download className="h-3.5 w-3.5" /> BAIXAR PNG
          </button>
          <button onClick={copy} disabled={busy} data-testid="copy-btn" className="inline-flex items-center justify-center gap-2 rounded-sm border border-primary/30 hover:bg-primary/10 text-primary font-mono tracking-widest text-[10px] px-4 py-3 disabled:opacity-50">
            {copied ? <><Check className="h-3.5 w-3.5" /> COPIADO</> : <><Copy className="h-3.5 w-3.5" /> COPIAR IMAGEM</>}
          </button>
        </div>
        <p className="text-center font-mono text-[10px] tracking-widest text-muted-foreground">
          POSTE NO X / DISCORD / TELEGRAM E DESAFIE OUTROS PILOTOS
        </p>
      </div>
    </div>
  );
}
